import { zodResolver } from "@hookform/resolvers/zod";
import { Button, DialogActions } from "@mui/material";
import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  CreateDDxIntegrationMutation,
  CreateDDxIntegrationMutationVariables,
  DDxExportIntegration,
  UpdateDDxIntegrationMutation,
  UpdateDDxIntegrationMutationVariables,
} from "~frontend/__generated__/graphql";
import {
  RhfErrorBanner,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import {
  CREATE_DDX_INTEGRATION,
  UPDATE_DDX_INTEGRATION,
} from "~frontend/graphql/integration";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

const createSchema = z.object({
  name: z.string().min(1, "Required"),
  organizationName: z.string().min(1, "Required"),
  apiKey: z.string().min(1, "Required"),
});

const updateSchema = z.object({
  name: z.string().min(1, "Required"),
  organizationName: z.string().min(1, "Required"),
  apiKey: z.string().optional(),
});

type CreateFormValues = z.infer<typeof createSchema>;
type UpdateFormValues = z.infer<typeof updateSchema>;

export function DDxIntegrationSettingsForm({
  integration,
  subaccountId,
  afterCreate,
  onClose,
}: {
  integration: DDxExportIntegration | null;
  subaccountId: string;
  afterCreate?: () => void;
  onClose: () => void;
}): ReactElement {
  const editing = !!integration;

  const {
    control,
    handleSubmit,
    setError,
    formState: { isDirty },
  } = useForm<CreateFormValues | UpdateFormValues>({
    resolver: zodResolver(editing ? updateSchema : createSchema),
    defaultValues: {
      name: integration?.name ?? "",
      organizationName: integration?.settings?.organizationName ?? "",
      apiKey: "",
    },
  });

  const [createDDxIntegration] = useMutation<
    CreateDDxIntegrationMutation,
    CreateDDxIntegrationMutationVariables
  >(CREATE_DDX_INTEGRATION, {
    refetchQueries: ["ListIntegrations"],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to create DDx integration",
  });

  const [updateDDxIntegration] = useMutation<
    UpdateDDxIntegrationMutation,
    UpdateDDxIntegrationMutationVariables
  >(UPDATE_DDX_INTEGRATION, {
    refetchQueries: ["ListIntegrations"],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to update DDx integration",
  });

  const getErrorMessage = useErrorMessageFormatter();

  async function onSubmit(values: CreateFormValues | UpdateFormValues) {
    try {
      if (editing) {
        const { errors } = await updateDDxIntegration({
          variables: {
            input: {
              integrationId: integration.id,
              name: values.name,
              organizationName: values.organizationName,
              apiKey: values.apiKey || null,
            },
          },
        });
        if (errors) {
          throw errors;
        }
        onClose();
      } else {
        const { errors } = await createDDxIntegration({
          variables: {
            input: {
              subaccountId,
              name: values.name,
              organizationName: values.organizationName,
              apiKey: (values as CreateFormValues).apiKey,
            },
          },
        });
        if (errors) {
          throw errors;
        }
        if (afterCreate) {
          afterCreate();
        }
        onClose();
      }
    } catch (e) {
      setError("root", { message: getErrorMessage(e) });
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RhfErrorBanner control={control} />
      <RhfTextField
        control={control}
        name="name"
        label="Name Your Integration"
        fullWidth
      />
      <RhfTextField
        control={control}
        name="organizationName"
        label="Organization Name"
        fullWidth
      />
      <RhfTextField
        control={control}
        name="apiKey"
        label={editing ? "API Key (leave blank to keep current)" : "API Key"}
        type="password"
        fullWidth
      />
      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          disabled={editing && !isDirty}
        >
          {editing ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </form>
  );
}
